import { api } from '../lib/api'

export type StripePayoutStatus = {
  stripe_connected: boolean
  charges_enabled: boolean
  payouts_enabled: boolean
  requirements_currently_due: string[]
  account_country: string | null
  default_currency: string | null
  bank_last4: string | null
  dashboard_url?: string | null
}

export type GenericEarningsSummary = {
  available_to_withdraw_usd_cents: number | null
  pending_usd_cents: number | null
  lifetime_earned_usd_cents: number
  lifetime_platform_fees_usd_cents: number
  net_lifetime_usd_cents: number
  last_payout: {
    amount_usd_cents: number | null
    created_at: string | null
  }
}

export type PayoutActivityItem = {
  id: number
  type: 'booking' | 'subscription' | 'fee' | 'transfer' | 'withdrawal' | 'adjustment'
  title: string
  subtitle: string | null
  amount_usd_cents: number
  created_at: string
}

export type PayoutActivityResponse = {
  items: PayoutActivityItem[]
  page: number
  page_size: number
  has_more: boolean
}

export type PlatformFeeRequirement = {
  key: string
  label: string
  op: string | null
  target: string | number | boolean | null
  current: string | number | boolean | null
  met: boolean
  display_current: string
  display_rule: string
  available?: boolean
}

export type PlatformFeeTierSnapshot = {
  tier: string | null
  tier_name: string | null
  fee_bps: number | null
  fee_percent: string | null
  locked_until?: string | null
  grace_until?: string | null
}

export type PlatformFeeReputationDriver = {
  key: string
  label: string
  value: number | string | null
  status: 'good' | 'warn' | 'bad' | 'na'
}

export type PlatformFeeReputation = {
  score: number | null
  status?: string | null
  level_name?: string | null
  updated_at?: string | null
  config_version?: number | null
  drivers?: PlatformFeeReputationDriver[]
}

export type PlatformFeeRoadmapItem = {
  tier: string
  tier_name: string
  fee_bps?: number | null
  fee_percent: string | null
  requirements: PlatformFeeRequirement[]
}

export type PlatformFeeProgram = {
  active: PlatformFeeTierSnapshot
  eligible: PlatformFeeTierSnapshot
  target_next: PlatformFeeTierSnapshot
  roadmap?: PlatformFeeRoadmapItem[]
  reputation?: PlatformFeeReputation
  requirements: PlatformFeeRequirement[]
  last_eval_at: string | null
  locked_from: string | null
  config_version?: number
}

export async function getPayoutsStripeStatus() {
  const { data } = await api.get<StripePayoutStatus>('/api/payouts/stripe/status/')
  return data
}

export async function getPayoutsSummary() {
  const { data } = await api.get<GenericEarningsSummary>('/api/payouts/summary/')
  return data
}

export async function getPayoutsActivity(page = 1, pageSize = 20) {
  const { data } = await api.get<PayoutActivityResponse>('/api/payouts/activity/', { params: { page, page_size: pageSize } })
  return data
}

export async function getPlatformFeeProgram() {
  const { data } = await api.get<PlatformFeeProgram>('/api/creator/payouts/platform-fee/')
  const anyData = data as any
  if (!anyData?.active || !anyData?.target_next || !anyData?.eligible) {
    return {
      active: {
        tier: anyData?.active_tier ?? null,
        tier_name: anyData?.active_tier_name ?? null,
        fee_bps: anyData?.active_fee_bps ?? null,
        fee_percent: anyData?.active_fee_bps != null ? (Number(anyData.active_fee_bps) / 100).toFixed(2) : null,
        locked_until: anyData?.locked_until ?? null,
        grace_until: anyData?.grace_until ?? null,
      },
      eligible: {
        tier: anyData?.preview_eligible_tier ?? anyData?.active_tier ?? null,
        tier_name: anyData?.preview_eligible_tier_name ?? anyData?.active_tier_name ?? null,
        fee_bps: anyData?.preview_fee_bps ?? anyData?.active_fee_bps ?? null,
        fee_percent: anyData?.preview_fee_bps != null ? (Number(anyData.preview_fee_bps) / 100).toFixed(2) : null,
      },
      target_next: {
        tier: anyData?.next_tier_target ?? null,
        tier_name: anyData?.next_tier_target_name ?? null,
        fee_bps: anyData?.next_tier_target_fee_bps ?? null,
        fee_percent: anyData?.next_tier_target_fee_bps != null ? (Number(anyData.next_tier_target_fee_bps) / 100).toFixed(2) : null,
      },
      roadmap: Array.isArray(anyData?.roadmap) ? anyData.roadmap : [],
      reputation: anyData?.reputation ?? {
        score: null,
        status: 'N/A',
        level_name: null,
        updated_at: null,
        config_version: null,
        drivers: [],
      },
      requirements: Array.isArray(anyData?.requirements) ? anyData.requirements : [],
      last_eval_at: anyData?.last_eval_at ?? anyData?.last_evaluated_at ?? null,
      locked_from: anyData?.locked_from ?? null,
      config_version: anyData?.config_version,
    } as PlatformFeeProgram
  }
  return {
    ...data,
    roadmap: Array.isArray(anyData?.roadmap) ? anyData.roadmap : [],
    reputation: anyData?.reputation ?? {
      score: null,
      status: 'N/A',
      level_name: null,
      updated_at: null,
      config_version: null,
      drivers: [],
    },
  } as PlatformFeeProgram
}

export async function startPayoutsConnect() {
  try {
    const { data } = await api.post<{ url: string }>('/api/payouts/connect/start/', {})
    return data
  } catch (e: any) {
    if (![404, 500].includes(Number(e?.response?.status))) throw e
    const { data } = await api.post<{ url: string }>('/api/stripe/connect/onboarding/', {})
    return data
  }
}

export async function createPayoutsOnboardingLink() {
  const { data } = await api.post<{ url: string }>('/api/payouts/stripe/onboarding-link/', {})
  return data
}

export async function syncPayoutsConnectStatus() {
  try {
    const { data } = await api.post<StripePayoutStatus>('/api/payouts/connect/sync/', {})
    return data
  } catch (e: any) {
    if (e?.response?.status !== 404) throw e
    return getPayoutsStripeStatus()
  }
}
